import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import AppLayout from '../components/AppLayout'
import TaskModal from '../components/TaskModal'
import { useApp } from '../context/AppContext'
import { useAuth } from '../context/AuthContext'
import { getDaysUntil, getInitials } from '../data/mockData'

const MealPlaceholder = ({ eventId }) => (
  <Link to={`/event/${eventId}/meals`} style={{ textDecoration: 'none' }}>
    <div style={{ fontSize: 12, color: '#DF83A9', textAlign: 'center', padding: '16px 0', cursor: 'pointer' }}>
      <p style={{ marginBottom: 8 }}>ingen retter planlagt</p>
      <p style={{ color: '#6c63ff', fontWeight: 500 }}>+ Tilføj ret</p>
    </div>
  </Link>
)

const GuestPlaceholder = ({ eventId }) => (
  <Link to={`/event/${eventId}/guests`} style={{ textDecoration: 'none' }}>
    <div style={{ fontSize: 12, color: '#DF83A9', textAlign: 'center', padding: '16px 0', cursor: 'pointer' }}>
      <p style={{ marginBottom: 8 }}>ingen gæster inviteret</p>
      <p style={{ color: '#6c63ff', fontWeight: 500 }}>+ Inviter gæster</p>
    </div>
  </Link>
)

export default function Dashboard() {
  const { user } = useAuth()
  const { tasks, toggleTask, events, guests, meals, budget, currentUser, invitations, acceptInvitation, declineInvitation } = useApp()
  const [taskModal, setTaskModal] = useState(null) // null | 'new' | task object
  const [calendarDate, setCalendarDate] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1))
  const myTasks = tasks.filter(t => t.assignedToId === currentUser.id)
  const firstEvent = events[0]
  const firstEventMeals = firstEvent ? (meals[firstEvent.id] || []) : []
  const firstEventGuests = firstEvent ? (guests[firstEvent.id] || []) : []
  const today = new Date()
  const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1)

  const getLightBackground = (color) => {
    const c = parseInt(color.slice(1), 16)
    const r = Math.min(255, Math.floor((c >> 16) + (255 - (c >> 16)) * 0.45))
    const g = Math.min(255, Math.floor(((c >> 8) & 0xff) + (255 - ((c >> 8) & 0xff)) * 0.45))
    const b = Math.min(255, Math.floor((c & 0xff) + (255 - (c & 0xff)) * 0.45))
    return '#' + (r << 16 | g << 8 | b).toString(16).padStart(6, '0')
  }

  const getDarkText = (color) => {
    switch(color) {
      case '#E8C1D1': return '#CD0D6D'
      case '#C9845A': return '#8D5C3F'
      case '#C08A4A': return '#D6852F'
      case '#F7B98A': return '#AD8261'
      case '#2271B1': return '#184F7C'
      case '#B4B48D': return '#405727'
      default: return '#222222'
    }
  }

  const bgMap = {}
  const textMap = {}
  events.forEach(event => {
    bgMap[event.color] = getLightBackground(event.color)
    textMap[event.color] = getDarkText(event.color)
  })

  // Mini calendar
  const year = today.getFullYear()
  const month = today.getMonth()
  const firstDay = new Date(year, month, 1).getDay()
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const startPad = firstDay === 0 ? 6 : firstDay - 1
  const monthName = today.toLocaleDateString('da-DK', { month: 'long', year: 'numeric' })

  const eventDays = events.reduce((acc, ev) => {
    const d = new Date(ev.date)
    if (d.getFullYear() === year && d.getMonth() === month) {
      acc[d.getDate()] = [...(acc[d.getDate()] || []), ev.color]
    }
    return acc
  }, {})

  return (
    <AppLayout>
      <div className="page-header">
        <div className="page-header-row1">
          <div className="page-header-left">
            <h1 className="page-title">Hej, {user?.displayName?.split(' ')[0] || 'Bruger'}</h1>
          </div>
          <div style={{ fontSize: 11, color: '#222222', fontWeight: 500, background: '#fff', padding: '6px 12px', borderRadius: 12, boxShadow: '0 1px 6px rgba(108,99,255,.07)' }}>
            Dagsdato: {today.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long' })}
          </div>
        </div>
        <p style={{ fontSize: 16, color: '#222222', marginBottom: 16 }}>Dette er dit personlige overblik over alle events</p>
      </div>

      <div className="page-content">
        {/* Countdown cards */}
        {events.length > 0 ? (
          <div className="grid-3">
            {events.map(event => {
              const days = getDaysUntil(event.date)
              const bg = bgMap[event.color] || '#f0eeff'
              const text = textMap[event.color] || '#3d35aa'
              return (
                <Link key={event.id} to={`/event/${event.id}`} className="countdown-card" style={{ background: bg }}>
                  <div className="countdown-bg-circle" style={{ background: event.color }} />
                  <div className="countdown-label" style={{ color: text }}>{event.name}</div>
                  <div className="countdown-days" style={{ color: text }}>{days}</div>
                  <div className="countdown-sublabel" style={{ color: text }}>dage tilbage</div>
                  <div className="countdown-date" style={{ color: text }}>
                    {new Date(event.date).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                </Link>
              )
            })}
          </div>
        ) : (
          <div className="card" style={{ padding: '40px 20px', textAlign: 'center' }}>
            <p style={{ fontSize: 14, color: '#DF83A9', marginBottom: 16 }}>Opret et event for at se oversigterne her</p>
          </div>
        )}

        {events.length > 0 && (
          <div className="dashboard-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12, marginBottom: 20 }}>
            {/* Budget oversigt */}
            <div className="card" style={{ gridColumn: 'span 2' }}>
              <div className="card-header">
                <span className="card-title">Budget oversigt</span>
              </div>
              <div className="budget-flex" style={{ display: 'flex', gap: 20, overflowX: 'auto', paddingBottom: 12 }}>
                {events.map(event => {
                  const budgetCats = budget && budget[event.id] ? budget[event.id] : []
                  const spent = budgetCats.reduce((s, c) => s + c.items.reduce((si, i) => si + i.amount, 0), 0)
                  const totalBudget = event.totalBudget || 0
                  const percentage = totalBudget > 0 ? Math.round((spent / totalBudget) * 100) : 0
                  const circumference = 2 * Math.PI * 45
                  const arcLength = circumference * (percentage / 100)

                  return (
                    <div key={event.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 'fit-content', flex: '0 0 auto' }}>
                      <p style={{ fontSize: 11, fontWeight: 600, color: '#1a1a2e', marginBottom: 12 }}>{event.name}</p>
                      <div style={{ position: 'relative', width: 120, height: 120, marginBottom: 8 }}>
                        <svg width="120" height="120" viewBox="0 0 100 100" style={{ transform: 'rotate(-90deg)' }}>
                          <circle cx="50" cy="50" r="45" fill="none" stroke="#ffffff" strokeWidth="10" />
                          <circle
                            cx="50" cy="50" r="45"
                            fill="none"
                            stroke={event.color}
                            strokeWidth="10"
                            strokeDasharray={`${arcLength} ${circumference}`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center' }}>
                          <div style={{ fontSize: 28, fontWeight: 700, color: '#1a1a2e', lineHeight: 1 }}>{percentage}%</div>
                          <div style={{ fontSize: 9, color: '#222222', marginTop: 2 }}>brugt</div>
                        </div>
                      </div>
                      <p style={{ fontSize: 9, color: '#222222', textAlign: 'center' }}>
                        {spent.toLocaleString('da-DK')} kr {totalBudget > 0 && `/ ${totalBudget.toLocaleString('da-DK')} kr`}
                      </p>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Pending invitations */}
            <div className="card invitations-card">
              <div className="card-header">
                <span className="card-title">Invitationer til events</span>
              </div>
              {invitations.length > 0 ? (
                invitations.slice(0, 3).map(inv => (
                  <div key={inv.id} style={{ padding: '10px 0', borderBottom: '1.5px solid #f0eeff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: inv.eventColor, flexShrink: 0 }} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <p style={{ fontSize: 11, fontWeight: 600, color: '#1a1a2e' }}>{inv.eventName}</p>
                        <p style={{ fontSize: 9, color: '#DF83A9', marginTop: 2 }}>fra {inv.fromName}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 10 }}>
                      <button type="button" onClick={async e => { e.stopPropagation(); try { await acceptInvitation(inv) } catch (err) { console.error('Accept error:', err) } }} style={{ background: '#6c63ff', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 9, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>ja</button>
                      <button type="button" onClick={async e => { e.stopPropagation(); try { await declineInvitation(inv.id) } catch (err) { console.error('Decline error:', err) } }} style={{ background: '#fff', color: '#DF83A9', border: 'none', borderRadius: 6, padding: '4px 8px', fontSize: 9, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>nej</button>
                    </div>
                  </div>
                ))
              ) : (
                <p style={{ fontSize: 12, color: '#DF83A9', textAlign: 'center', padding: '16px 0' }}>ingen ventende invitationer</p>
              )}
            </div>

            {/* This week's tasks */}
            <div className="card tasks-card">
              <div className="card-header">
                <span className="card-title">Ugens opgaver</span>
              </div>
              {(() => {
                const today = new Date()
                const startOfWeek = new Date(today)
                startOfWeek.setDate(today.getDate() - today.getDay() + 1)
                const endOfWeek = new Date(startOfWeek)
                endOfWeek.setDate(startOfWeek.getDate() + 6)

                const weekTasks = tasks.filter(t => {
                  if (!t.deadline) return false
                  const taskDate = new Date(t.deadline)
                  return taskDate >= startOfWeek && taskDate <= endOfWeek
                }).slice(0, 3)

                return weekTasks.length > 0 ? (
                  weekTasks.map((task, idx) => {
                    const taskEventColor = events.find(e => e.id === task.eventId)?.color || '#6c63ff'
                    const taskTextColor = textMap[taskEventColor] || '#1a1a2e'
                  return (
                      <div key={task.id}>
                        <div
                          className="todo-item"
                          onClick={() => setTaskModal(task)}
                          style={{
                            cursor: 'pointer',
                            borderBottom: 'none',
                            transition: 'background 0.15s'
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#ede8e0'}
                          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                        >
                          <div
                            className={`checkbox ${task.done ? 'checked' : ''}`}
                            onClick={e => { e.stopPropagation(); toggleTask(task.id) }}
                          >
                            {task.done && <svg width="9" height="7" viewBox="0 0 9 7" fill="none"><path d="M1 3.5l2.5 2.5 4.5-5" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className={`todo-text ${task.done ? 'done' : ''}`}>{task.title}</div>
                            <div className="todo-meta">
                              {task.eventColor && <div style={{ width: 6, height: 6, borderRadius: '50%', background: taskEventColor, flexShrink: 0 }} />}
                              <span style={{ fontSize: 10, color: taskTextColor }}>{task.eventName || ''}</span>
                            </div>
                          </div>
                        </div>
                        {idx < weekTasks.length - 1 && <div style={{ height: '1.5px', background: '#e8e3dc', margin: '10px 0' }} />}
                      </div>
                    )
                  })
                ) : (
                  <div style={{ fontSize: 12, color: '#DF83A9', textAlign: 'center', padding: '16px 0' }}>
                    <p>ingen opgaver denne uge</p>
                  </div>
                )
              })()}
            </div>

            {/* Gæster oversigt per event */}
            <div className="card guests-overview-card" style={{ gridColumn: 'span 2' }}>
              <div className="card-header">
                <span className="card-title">Gæster oversigt</span>
              </div>
              <div className="guests-flex" style={{ display: 'flex', overflowX: 'auto', paddingBottom: 12 }}>
                {events.map((event, idx) => {
                  const eventGuests = guests[event.id] || []
                  const confirmed = eventGuests.filter(g => g.status === 'confirmed').length
                  const pending = eventGuests.filter(g => g.status === 'pending').length
                  const declined = eventGuests.filter(g => g.status === 'declined').length
                  const eventNameColor = textMap[event.color] || '#1a1a2e'
                  return (
                    <div key={event.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 'fit-content', flex: '0 0 auto', padding: '0 16px', borderRight: idx < events.length - 1 ? '1.5px solid #e8e3dc' : 'none' }}>
                      <p style={{ fontSize: 10, fontWeight: 600, color: eventNameColor, marginBottom: 12 }}>{event.name}</p>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 32, fontWeight: 700, color: '#222222', lineHeight: 1, marginBottom: 4 }}>{confirmed}</div>
                        <p style={{ fontSize: 9, color: '#222222', marginBottom: 12, fontWeight: 500 }}>accepteret</p>
                        {pending > 0 && (
                          <>
                            <div style={{ fontSize: 32, fontWeight: 700, color: '#222222', lineHeight: 1, marginBottom: 4 }}>{pending}</div>
                            <p style={{ fontSize: 9, color: '#222222', fontWeight: 500 }}>afventer</p>
                          </>
                        )}
                        {declined > 0 && (
                          <>
                            <div style={{ fontSize: 32, fontWeight: 700, color: '#222222', lineHeight: 1, marginBottom: 4, marginTop: 8 }}>{declined}</div>
                            <p style={{ fontSize: 9, color: '#222222', fontWeight: 500 }}>afslået</p>
                          </>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Tidsplan Calendar */}
            <div className="card calendar-card" style={{ gridColumn: '1 / -1' }}>
              <div className="card-header" style={{ marginBottom: 16 }}>
                <span className="card-title">Tidsplan</span>
              </div>
              {(() => {
                const year = calendarDate.getFullYear()
                const month = calendarDate.getMonth()
                const daysInMonth = new Date(year, month + 1, 0).getDate()
                const firstDay = new Date(year, month, 1).getDay()
                const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1
                const monthName = calendarDate.toLocaleDateString('da-DK', { month: 'long', year: 'numeric' })
                const today = new Date()

                const days = []
                for (let i = 0; i < adjustedFirstDay; i++) days.push(null)
                for (let i = 1; i <= daysInMonth; i++) days.push(i)
                while (days.length < 42) days.push(null)

                const tasksByDate = tasks.reduce((acc, task) => {
                  if (task.deadline) {
                    const d = new Date(task.deadline)
                    if (d.getFullYear() === year && d.getMonth() === month) {
                      const dateKey = d.getDate()
                      if (!acc[dateKey]) acc[dateKey] = []
                      acc[dateKey].push(task)
                    }
                  }
                  return acc
                }, {})

                const eventDates = events.reduce((acc, ev) => {
                  const d = new Date(ev.date)
                  if (d.getFullYear() === year && d.getMonth() === month) {
                    acc[d.getDate()] = ev
                  }
                  return acc
                }, {})

                return (
                  <div className="calendar-container">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <button onClick={() => setCalendarDate(new Date(year, month - 1, 1))} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#1a1a2e', fontWeight: 600, padding: 0 }}>&lt;</button>
                      <p style={{ fontSize: 12, fontWeight: 600, color: '#1a1a2e', margin: 0, minWidth: 150, textAlign: 'center' }}>{monthName}</p>
                      <button onClick={() => setCalendarDate(new Date(year, month + 1, 1))} style={{ background: 'none', border: 'none', fontSize: 16, cursor: 'pointer', color: '#1a1a2e', fontWeight: 600, padding: 0 }}>&gt;</button>
                    </div>

                    <div className="calendar-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
                      {['man', 'tir', 'ons', 'tor', 'fre', 'lør', 'søn'].map(day => (
                        <div key={day} style={{ textAlign: 'center', fontSize: 8, fontWeight: 600, color: '#CD0D6D', paddingBottom: 4 }}>{day}</div>
                      ))}
                      {days.map((day, idx) => {
                        const isToday = day === today.getDate() && year === today.getFullYear() && month === today.getMonth()
                        const isEventDay = day && eventDates[day]
                        const dayTasks = day ? (tasksByDate[day] || []) : []

                        return (
                          <div key={idx} className="calendar-day-cell" style={{
                            aspectRatio: '1.2 / 1',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            padding: 4,
                            background: 'transparent',
                            border: 'none',
                            borderRadius: 0,
                            minHeight: 45,
                            position: 'relative'
                          }}>
                            {day ? (
                              <>
                                <div style={{ fontWeight: isToday ? 700 : 600, color: '#222222', fontSize: 11, marginBottom: 3 }}>
                                  {day}
                                </div>
                                {isEventDay && (
                                  <div style={{
                                    width: 4,
                                    height: 4,
                                    borderRadius: '50%',
                                    background: eventDates[day].color,
                                    marginBottom: 2
                                  }} />
                                )}
                                {dayTasks.length > 0 && (
                                  <div className="calendar-day-tasks" style={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', alignItems: 'center' }}>
                                    {dayTasks.map(task => {
                                      const taskColor = task.eventColor || events.find(e => e.id === task.eventId)?.color || '#6c63ff'
                                      const taskBg = bgMap[taskColor] || taskColor + '33'
                                      const taskText = textMap[taskColor] || '#1a1a2e'
                                      return (
                                        <div key={task.id} onClick={() => setTaskModal(task)} style={{
                                          background: taskBg,
                                          color: taskText,
                                          fontSize: 7,
                                          fontWeight: 600,
                                          padding: '1px 3px',
                                          borderRadius: 2,
                                          maxWidth: '95%',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          cursor: 'pointer'
                                        }} title={task.title}>
                                          {task.title}
                                        </div>
                                      )
                                    })}
                                  </div>
                                )}
                              </>
                            ) : null}
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )
              })()}
            </div>
          </div>
        )}
      </div>

      {taskModal && taskModal !== 'new' && <TaskModal task={taskModal} onClose={() => setTaskModal(null)} />}
      {taskModal === 'new' && <TaskModal onClose={() => setTaskModal(null)} />}
    </AppLayout>
  )
}
